import { buildApiUrl, getRegionFromUrl } from './utils/regionHelper';

// Types de rôles disponibles
export enum UserRole {
  ADMINISTRATEUR = 'ADMINISTRATEUR',
  VIP = 'VIP',
  AGENT_IDENTIFICATION = 'AGENT_IDENTIFICATION',
  AGENT_SAISIE = 'AGENT_SAISIE',
  AGENT_CALL = 'AGENT_CALL',
  AGENT_CALL_PROSPECTION = 'AGENT_CALL_PROSPECTION',
  AGENT_CALL_QUALIFICATION = 'AGENT_CALL_QUALIFICATION',
  BENEVOLE_MOBILISATION = 'BENEVOLE_MOBILISATION',
  VISUALISATION = 'VISUALISATION',
  // Roles CEGEL - Branche Technique (uniformises 12 Dec 2025)
  TIC_CEGEL_REGIONAL = 'TIC_CEGEL_REGIONAL',
  TIC_CEGEL_DEPARTEMENTAL = 'TIC_CEGEL_DEPARTEMENTAL',
  TIC_CEGEL_ZONE = 'TIC_CEGEL_ZONE',
  PRESIDENT_CEGEL_REGIONAL = 'PRESIDENT_CEGEL_REGIONAL',
  PRESIDENT_CEGEL_DEPARTEMENTAL = 'PRESIDENT_CEGEL_DEPARTEMENTAL',
  PRESIDENT_CEGEL_ZONE = 'PRESIDENT_CEGEL_ZONE',
  // Roles CEGEL legacy (compatibilite ABOBO)
  CEGEL_REGIONAL = 'CEGEL_REGIONAL',
  CEGEL_DEPARTEMENTAL = 'CEGEL_DEPARTEMENTAL',
  CEGEL_ZONE = 'CEGEL_ZONE',
  CEGEL_LOCAL = 'CEGEL_LOCAL'
}

// Interface utilisateur
export interface User {
  id: number;
  username: string;
  nom_complet: string;
  role: UserRole;
  email?: string;
  telephone?: string;
  modules_autorises?: string[];
}

class AuthService {
  private currentUser: User | null = null;
  private token: string | null = null;
  private _requirePasswordChange: boolean = false;

  constructor() {
    this.loadFromStorage();
  }

  get requirePasswordChange(): boolean {
    return this._requirePasswordChange;
  }

  setRequirePasswordChange(value: boolean) {
    this._requirePasswordChange = value;
  }

  // Méthode publique pour sauvegarder les credentials (utilisée par Login.tsx pour SSO agents)
  saveCredentials(token: string, user: User, requirePasswordChange: boolean = false) {
    this.token = token;
    this.currentUser = user;
    this._requirePasswordChange = requirePasswordChange;
    this.saveToStorage();

    // Sauvegarder aussi le flag de changement de mot de passe pour les autres modules
    if (requirePasswordChange) {
      localStorage.setItem('requirePasswordChange', 'true');
    } else {
      localStorage.removeItem('requirePasswordChange');
    }

    console.log('[AuthService] Credentials sauvegardés pour SSO:', user.username, 'requirePasswordChange:', requirePasswordChange);
  }

  loadFromStorage() {
    // Vérifier toutes les clés possibles
    const conquestToken = localStorage.getItem('conquest_token');
    const normalToken = localStorage.getItem('token');
    const gbekToken = localStorage.getItem('gbeke_token');
    
    // Essayer conquest_token en premier, puis les autres
    const savedToken = conquestToken || normalToken || gbekToken;
    
    const conquestUser = localStorage.getItem('conquest_user');
    const normalUser = localStorage.getItem('user');
    const gbekUser = localStorage.getItem('gbeke_user');
    
    // Essayer conquest_user en premier, puis les autres
    const savedUser = conquestUser || normalUser || gbekUser;
    
    if (savedToken && savedUser) {
      this.token = savedToken;
      try {
        this.currentUser = JSON.parse(savedUser);

        // Synchroniser dans TOUTES les clés pour assurer la persistance SSO
        localStorage.setItem('conquest_token', savedToken);
        localStorage.setItem('token', savedToken);
        localStorage.setItem('gbeke_token', savedToken);
        localStorage.setItem('conquest_user', savedUser);
        localStorage.setItem('user', savedUser);
        localStorage.setItem('gbeke_user', savedUser);
      } catch (e) {
        console.error('[AuthService] Error parsing user data:', e);
      }
    }
  }

  private saveToStorage() {
    if (this.token) {
      localStorage.setItem('conquest_token', this.token);
      // Sauvegarder aussi dans les autres clés pour compatibilité
      localStorage.setItem('token', this.token);
      localStorage.setItem('gbeke_token', this.token);
    }
    if (this.currentUser) {
      const userStr = JSON.stringify(this.currentUser);
      localStorage.setItem('conquest_user', userStr);
      // Sauvegarder aussi dans les autres clés pour compatibilité  
      localStorage.setItem('user', userStr);
      localStorage.setItem('gbeke_user', userStr);
    }
  }

  private clearStorage() {
    localStorage.removeItem('conquest_token');
    localStorage.removeItem('conquest_user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('gbeke_token');
    localStorage.removeItem('gbeke_user');
  }

  async login(username: string, password: string): Promise<User> {
    try {
      // Use buildApiUrl for multi-region gateway compatibility
      const API_URL = buildApiUrl('/api/auth/login');
      console.log('[AuthService] Login via gateway:', API_URL);
      
      // OTP : envoyer le token d'appareil de confiance (par identifiant) si present
      const deviceToken = localStorage.getItem('conquete_dt_' + username) || '';
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(deviceToken ? { 'X-Device-Token': deviceToken } : {}),
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      // ✅ CORRECTIF 6: Gérer la réponse 403 (accès interdit au Portail)
      // Note: La gestion du 403 est maintenant faite dans Login.tsx avec le RedirectModal
      if (response.status === 403 && data.redirectUrl) {
        console.warn('[AuthService] Accès Portail interdit:', data.role);
        console.log('[AuthService] Redirection gérée par Login.tsx');
        throw new Error(data.message || 'Accès interdit au Portail');
      }

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Erreur de connexion');
      }

      this.token = data.token;
      this.currentUser = data.user;
      this._requirePasswordChange = data.requirePasswordChange || false;
      this.saveToStorage();

      return data.user;
    } catch (error: any) {
      throw new Error(error.message || 'Erreur de connexion');
    }
  }

  logout() {
    this.currentUser = null;
    this.token = null;
    this._requirePasswordChange = false;
    this.clearStorage();
  }

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    if (!this.token) return false;
    
    try {
      // JWT format: header.payload.signature
      const parts = this.token.split('.');
      if (parts.length !== 3) return false;
      
      // Décoder le payload (partie du milieu)
      const payload = JSON.parse(atob(parts[1]));
      
      // Vérifier l'expiration (exp est en secondes, Date.now() en millisecondes)
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  }

  hasPermission(permission: string): boolean {
    return true; // Simplifié pour le test
  }

  // Regle sous-domaine vote.conquete.app (campagne Vote meilleure commune) : tout le monde va
  // directement dans Mobilisation, sans passer par l'accueil du portail ni ses statistiques.
  private estHoteVote(): boolean {
    return typeof window !== 'undefined' && window.location.hostname.toLowerCase().startsWith('vote.');
  }

  getRedirectionUrl(): string {
    if (!this.currentUser) return '/login';
    if (this.estHoteVote()) {
      const r = getRegionFromUrl();
      return (r ? `/${r}` : '') + '/mobilisation';
    }
    // Utiliser le préfixe de région actuel
    const region = getRegionFromUrl();
    const regionPrefix = region ? `/${region}` : '';

    // AGENT_CALL -> Mobilisation
    if (this.currentUser.role === UserRole.AGENT_CALL) {
      return `${regionPrefix}/mobilisation`;
    }

    // AGENT_IDENTIFICATION et autres agents -> Identification
    return `${regionPrefix}/identification`;
  }

  shouldShowPortal(): boolean {
    if (!this.currentUser) return false;
    if (this.estHoteVote()) return false; // sous-domaine vote : jamais l'accueil du portail
    // Ajouter les délégués et autres rôles autorisés à voir le portail
    const portalRoles = [
      UserRole.ADMINISTRATEUR,
      UserRole.VIP,
      UserRole.VISUALISATION,
      'COORDINATEUR',
      'DELEGUE_DEPARTEMENTAL',
      'DELEGUE_ZONE',
      'DELEGUE_SECTION',
      // Roles CEGEL - Branche Technique (12 Dec 2025)
      UserRole.TIC_CEGEL_REGIONAL,
      UserRole.TIC_CEGEL_DEPARTEMENTAL,
      UserRole.TIC_CEGEL_ZONE,
      UserRole.PRESIDENT_CEGEL_REGIONAL,
      UserRole.PRESIDENT_CEGEL_DEPARTEMENTAL,
      UserRole.PRESIDENT_CEGEL_ZONE,
      // Roles CEGEL legacy (compatibilite ABOBO)
      UserRole.CEGEL_REGIONAL,
      UserRole.CEGEL_DEPARTEMENTAL,
      UserRole.CEGEL_ZONE,
      UserRole.CEGEL_LOCAL
    ];
    return portalRoles.includes(this.currentUser.role as any);
  }

  canManageUsers(): boolean {
    return this.currentUser?.role === UserRole.ADMINISTRATEUR;
  }
}

export const authService = new AuthService();